import { Routes, Route, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getMyPermissions } from "./api/users";
import ScorecardPage from "./pages/ScorecardPage";
import { Layout } from "./components/Layout";
import { OperatorRoute, AgentPanelRoute } from "./components/AgentRoute";
import { useAuth } from "./auth/AuthContext";
import { LandingPage } from "./pages/LandingPage";
import { SignupPage } from "./pages/SignupPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { OnboardingReviewPage } from "./pages/OnboardingReviewPage";
import { LoginPage } from "./pages/LoginPage";
import { AgentLoginPage } from "./pages/AgentLoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CampaignsPage } from "./pages/CampaignsPage";
import { ContactsPage } from "./pages/ContactsPage";
import { RequestsPage } from "./pages/RequestsPage";
import { ChangelogPage } from "./pages/ChangelogPage";
import { AssistantPage } from "./pages/AssistantPage";
import { ReportsPage } from "./pages/ReportsPage";
import { DidsPage } from "./pages/DidsPage";
import { DidAcquisitionPage } from "./pages/DidAcquisitionPage";
import { CallerIdGroupsPage } from "./pages/CallerIdGroupsPage";
import { UsersPage } from "./pages/UsersPage";
import { ExtensionsPage } from "./pages/ExtensionsPage";
import { ClientsPage } from "./pages/ClientsPage";
import { RecordingsPage } from "./pages/RecordingsPage";
import CallRecordingsPage from "./pages/CallRecordingsPage";
import { TrunksPage } from "./pages/TrunksPage";
import { PlansPage } from "./pages/PlansPage";
import { InvoicingPage } from "./pages/InvoicingPage";
import { DestinationsPage } from "./pages/DestinationsPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";
import { RoutesPage } from "./pages/RoutesPage";
import { SettingsPage } from "./pages/SettingsPage";
import { GeographyPage } from "./pages/GeographyPage";
import { IvrDesignerPage } from "./pages/IvrDesignerPage";
import { MyAgentsPage } from "./pages/MyAgentsPage";
import { QueueStatusPage } from "./pages/QueueStatusPage";
import { TransferDestinationsPage } from "./pages/TransferDestinationsPage";
import { EvaluationPage } from "./pages/EvaluationPage";
import { TimeslotsPage } from "./pages/TimeslotsPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { ActivityPage } from "./pages/ActivityPage";
import { DncPage } from "./pages/DncPage";
import { RolesPage } from "./pages/RolesPage";
import { BillingPage } from "./pages/BillingPage";
import { DialoguesPage } from "./pages/DialoguesPage";
import { DocumentsPage } from "./pages/DocumentsPage";
import { MusicOnHoldPage } from "./pages/MusicOnHoldPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { KycPage } from "./pages/KycPage";
import { NumberLookupPage } from "./pages/NumberLookupPage";
import { RatesPage } from "./pages/RatesPage";
import { FcrPage } from "./pages/FcrPage";
import { DidComplaintsPage } from "./pages/DidComplaintsPage";
import { DidSpamDetectionPage } from "./pages/DidSpamDetectionPage";
import { ConsentPage } from "./pages/ConsentPage";
import { WorkforcePage } from "./pages/WorkforcePage";
import { LeadsPage } from "./pages/LeadsPage";
import { MessagesPage } from "./pages/MessagesPage";

// Logged-out visitors see the public marketing site at "/"; logged-in
// operators are sent straight to their dashboard, and agent sessions to
// their one reachable route.
function HomeRoute() {
  const { isAuthenticated, user } = useAuth();
  // Read from the same endpoint the nav uses, so the landing decision and the
  // visible links can never disagree.
  const { data: perms } = useQuery({
    queryKey: ["me", "permissions"],
    queryFn: getMyPermissions,
    enabled: isAuthenticated && user?.principal !== "agent",
    staleTime: 60_000,
  });
  if (!isAuthenticated) return <LandingPage />;
  if (user?.principal === "agent") return <Navigate to="/agent" replace />;
  // A floor-supervisor login cannot load the dashboard: it needs the campaign
  // list, which carries the caller IDs we dial from and is deliberately closed
  // to them. Send them to the screen they actually exist to watch instead of to
  // a page that would 403 on arrival.
  if (perms?.is_restricted) return <Navigate to="/queue-status" replace />;
  return <Navigate to="/dashboard" replace />;
}

// `/agent` is reachable by BOTH principals (operator shared-login and an
// agent's own token), with different chrome per principal, and honest
// "your session ended" messaging for an agent whose session really did end
// — see AgentPanelRoute's own comment in components/AgentRoute.tsx for why
// that logic lives there rather than inline here.

export function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<HomeRoute />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/apply" element={<OnboardingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/agent-login" element={<AgentLoginPage />} />
      {/* Shared by both principals — see AgentPanelRoute's own comment. */}
      <Route path="/agent" element={<AgentPanelRoute />} />
      {/* Authenticated admin */}
      <Route
        element={
          <OperatorRoute>
            <Layout />
          </OperatorRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/workforce" element={<WorkforcePage />} />
        <Route path="/scorecard" element={<ScorecardPage />} />
        <Route path="/rates" element={<RatesPage />} />
        <Route path="/requests" element={<RequestsPage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/assistant" element={<AssistantPage />} />
        <Route path="/dids" element={<DidsPage />} />
        <Route path="/dids/acquire" element={<DidAcquisitionPage />} />
        <Route path="/dids/complaints" element={<DidComplaintsPage />} />
        <Route path="/dids/spam-detection" element={<DidSpamDetectionPage />} />
        <Route path="/consent-sms" element={<ConsentPage />} />
        <Route path="/callerid-groups" element={<CallerIdGroupsPage />} />
        <Route path="/number-lookup" element={<NumberLookupPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/extensions" element={<ExtensionsPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/recordings" element={<RecordingsPage />} />
        <Route path="/call-recordings" element={<CallRecordingsPage />} />
        <Route path="/trunks" element={<TrunksPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/invoicing" element={<InvoicingPage />} />
        <Route path="/destinations" element={<DestinationsPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/onboarding" element={<OnboardingReviewPage />} />
        <Route path="/routes" element={<RoutesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/geography" element={<GeographyPage />} />
        <Route path="/ivr" element={<IvrDesignerPage />} />
        {/* /agent itself is registered once, above, outside this block — see
            AgentPanelRoute's comment for why it can't also live here. */}
        <Route path="/agents" element={<MyAgentsPage />} />
        <Route path="/queue-status" element={<QueueStatusPage />} />
        <Route path="/leads" element={<LeadsPage />} />
        <Route path="/transfer-destinations" element={<TransferDestinationsPage />} />
        <Route path="/evaluation" element={<EvaluationPage />} />
        <Route path="/timeslots" element={<TimeslotsPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/dnc" element={<DncPage />} />
        <Route path="/roles" element={<RolesPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/dialogues" element={<DialoguesPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/musiconhold" element={<MusicOnHoldPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/kyc" element={<KycPage />} />
        <Route path="/fcr" element={<FcrPage />} />
      </Route>
    </Routes>
  );
}
